import React from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import Button, { ButtonPurpose } from "shared/Button";
import Loader from "shared/Loader";
import { notes as notesApi, errorMessage } from "shared/api";
import styles from "./NotesPanel.module.css";

const PAGE_SIZE = 5;

const formatTimestamp = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * IBO-scoped notes panel. Shows the most recent notes for the selected IBO,
 * five at a time with a "View more" affordance, and lets the rep add a new
 * note. Backed by the mock notes REST API (shared/api/notes) so it swaps to
 * the real endpoint without UI changes.
 */
export function NotesPanel({ iboId }) {
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [visible, setVisible] = React.useState(PAGE_SIZE);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [draft, setDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const reqIdRef = React.useRef(0);

  const load = React.useCallback(
    (count) => {
      if (!iboId) return;
      const reqId = ++reqIdRef.current;
      setLoading(true);
      setError(null);
      notesApi
        .list({ iboId, page: 1, pageSize: count })
        .then((res) => {
          if (reqId !== reqIdRef.current) return;
          setItems(res.items || []);
          setTotal(res.total || 0);
          setLoading(false);
        })
        .catch((err) => {
          if (reqId !== reqIdRef.current) return;
          setError(errorMessage(err, "Couldn't load the notes."));
          setLoading(false);
        });
    },
    [iboId]
  );

  React.useEffect(() => {
    setVisible(PAGE_SIZE);
    load(PAGE_SIZE);
  }, [iboId, load]);

  const handleViewMore = () => {
    const next = visible + PAGE_SIZE;
    setVisible(next);
    load(next);
  };

  const handleAddNote = () => {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    notesApi
      .create({ iboId, text })
      .then(() => {
        setDraft("");
        // Keep the freshly added note visible by growing the window by one.
        const next = visible + 1;
        setVisible(next);
        load(next);
        setSaving(false);
        toast.success("Note added.");
      })
      .catch((err) => {
        setSaving(false);
        toast.error(errorMessage(err, "Couldn't add the note."));
      });
  };

  const hasMore = total > visible;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={styles.heading}>Notes</h3>
        <span className={styles.count}>
          {total} {total === 1 ? "note" : "notes"}
        </span>
      </header>

      <div className={styles.composer}>
        <textarea
          className={styles.textarea}
          placeholder="Add a note about this IBO..."
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={2}
        />
        <Button
          type="button"
          purpose={ButtonPurpose.save}
          title="Save this note to the IBO's record."
          onClick={handleAddNote}
          disabled={!draft.trim() || saving}
        >
          Add Note
        </Button>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {loading && !items.length ? (
        <div className={styles.loaderWrap}>
          <Loader size={36} />
        </div>
      ) : items.length ? (
        <ul className={styles.list}>
          {items.map((note, index) => (
            <li key={`${note.id || "note"}-${index}`} className={styles.note}>
              <div className={styles.noteMeta}>
                <span className={styles.author}>{note.author}</span>
                <span className={styles.timestamp}>
                  {formatTimestamp(note.createdAt)}
                </span>
              </div>
              {note.orderId && (
                <span className={styles.orderTag}>Order {note.orderId}</span>
              )}
              <p className={styles.noteText}>{note.text}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.empty}>No notes yet for this IBO.</p>
      )}

      {hasMore && (
        <div className={styles.viewMore}>
          <Button
            type="button"
            title="Load more notes for this IBO."
            onClick={handleViewMore}
            disabled={loading}
          >
            View more
          </Button>
        </div>
      )}
    </section>
  );
}

NotesPanel.propTypes = {
  iboId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default NotesPanel;
